import clsx from 'clsx'
import Router from 'next/router'
import PropTypes from 'prop-types'
import React from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { connect } from 'react-redux'
import compose from 'recompose/compose'
import Button from 'components/common/button'
import CommonLayout from 'components/layout/common'
import Loader from 'components/loader'
import lookbookDuck from 'components/lookbook/lookbook-item/duck'
import ProductList from 'components/product/product-list'
import env from 'config/env'
import { BUTTON_TYPES } from 'constants/button'
import Typo from 'constants/typography'
import LookbookApiHandler from 'lib/api/lookbook'
import ProductsApiHandler from 'lib/api/products'
import { withI18next } from 'lib/i18n/withI18next'
import { segmentTrackAction } from 'lib/segment/index'
import { EVENT } from 'lib/segment/const'
import StateStorage from 'lib/state-storage'
import Utils from 'lib/utils'
import { redirectToHomePage } from 'lib/utils/handle-response'
import { hasLocizeTranslation } from 'lib/utils/locize'
import { PAGE_NAME } from 'pages/const'
import styles from './style.scss'
import LookbookHeader from './header'
import LookbookContentItem from './content-item'
import BasePage from '../base'

class Lookbook extends BasePage {
  static async getInitialProps({
    store,
    req,
    query,
    res,
    isServer,
    user_uid,
    segment_anonymous_id,
    device,
  }) {
    if (isServer) {
      BasePage.loadInitialContentFromServer({ req, res, store })
    } else {
      BasePage.loadInitialContentFromClient(req, res, store)
    }

    const lookBookInitialContent = await Lookbook.getInitialContent({
      store,
      query,
      req,
      res,
      user_uid,
      segment_anonymous_id,
      device,
    }).catch((error) => BasePage.onPageError(error))
    return lookBookInitialContent
  }

  static async getInitialContent({
    store,
    query,
    req,
    res,
    user_uid,
    segment_anonymous_id,
    device,
  }) {
    const id =
      (req && req.params && req.params.id ? req.params.id : query.id) || 1
    const shopSlug =
      req && req.params && req.params.zone ? req.params.zone : query.zone
    const languageSlug =
      req && req.params && req.params.lang ? req.params.lang : query.lang

    const lookbookOptions = {
      id,
      shopSlug,
      languageSlug,
      ...(Boolean(user_uid) && { userUId: user_uid }),
      ...(Boolean(segment_anonymous_id) && {
        segmentAnonymousID: segment_anonymous_id,
      }),
      ...(Boolean(device) && { device }),
    }

    return LookbookApiHandler.get(lookbookOptions).then((data) => {
      if (!data) {
        redirectToHomePage(res)
      }

      store.dispatch(lookbookDuck.creators.receive(data))
      return data
    })
  }

  state = {
    allProducts: null,
    allProductsPage: 0,
    hasMore: true,
  }

  afterPageMount() {
    if (typeof window !== 'undefined') {
      setTimeout(() => window.scrollTo(0, 0), 500)
    }

    const { lookbook } = this.props

    this.loadMoreAllProducts(lookbook && lookbook.product_category_id)
    if (lookbook) {
      this.trackEvent(lookbook)
    }
  }

  trackEvent(lookbook) {
    const user = StateStorage.getAuthUser() || StateStorage.getAnonymousUser()
    const data = {
      id_customer: user?.id_customer,
      id_lookbook: lookbook.id_lookbook,
      lookbook_name: lookbook.banner.title,
    }

    segmentTrackAction(EVENT.lookbookViewed, data)
  }

  loadMoreAllProducts(categoryId) {
    if (this.loadingMoreProducts) {
      return
    }

    if (!categoryId) {
      this.setState({ allProducts: [] })
      return
    }

    this.loadingMoreProducts = true

    ProductsApiHandler.getList({
      categoryId,
      currencyId: this.props.internationalization.currency,
      shopId: this.props.internationalization.shop,
      skip: this.state.allProductsPage * 20,
    }).then((res) => {
      this.loadingMoreProducts = false

      const curAllProducts = this.state.allProducts || []
      const newAllProducts = Utils.isArray(res.products) ? res.products : []

      this.setState({
        allProducts: [...curAllProducts, ...newAllProducts],
        allProductsPage: this.state.allProductsPage + 1,
        hasMore:
          curAllProducts.length < curAllProducts.length + newAllProducts.length,
      })
    })
  }

  getPageMeta() {
    const { lookbook } = this.props
    let title = ''
    let description = ''
    let image = ''

    if (lookbook?.banner) {
      const {
        description: lookbookDescription,
        image_cover_mobile,
        page_title,
        title: lookbookTitle,
      } = lookbook.banner

      title = `${
        page_title || lookbookTitle || 'Lookbook'
      } @ Pomelo Fashion - Pomelo`
      description = lookbookDescription
      image = `${env.IMG_HOST}img/lookbooks/${image_cover_mobile}.jpg`
    } else {
      title = 'Lookbook @ Pomelo Fashion - Pomelo'
      description =
        'Shop the latest fashion at POMELO fashion. Designed and manufactured in house for quality and style. Dresses, tops, knits, jackets, denim, accessories and more!'
    }

    return {
      title,
      description,
      image,
    }
  }

  renderAllProducts() {
    let body
    const { lookbook, t, isPhone } = this.props
    const { allProducts } = this.state
    const hasTitle = !!(
      lookbook.banner &&
      (lookbook.banner.title || lookbook.banner.description)
    )

    if (allProducts?.length) {
      body = (
        <InfiniteScroll
          next={() =>
            this.loadMoreAllProducts(lookbook && lookbook.product_category_id)
          }
          dataLength={allProducts.length}
          scrollThreshold={0.9}
          loader={
            <div className="loader-wrapper">
              <Loader />
            </div>
          }
          hasMore={this.state.hasMore}
        >
          <ProductList
            displayBadges={false}
            displayDescription
            displayPrice
            list={allProducts}
            refId={lookbook.id}
            refType="lookbook_shop"
            promptLocation="wishlist_category"
            id_lookbook={lookbook.id_lookbook}
            lookbook_name={lookbook.banner?.title}
          />
        </InfiniteScroll>
      )
    } else {
      body = (
        <div className="loader-wrapper">
          <Loader />
        </div>
      )
    }

    return (
      <div className="lookbook-all-products">
        {hasTitle && (
          <span className={clsx('title', isPhone ? Typo.h6 : Typo.h4)}>
            {t('Shop The Lookbook')}
          </span>
        )}
        {body}
      </div>
    )
  }

  redirectToInfluencerSignup() {
    Router.push(`https://signup.partnerize.com/signup/en/pomelofashion`)
  }

  renderContents() {
    const {
      isPhone,
      lookbook,
      lookbook: { content: items, banner } = {},
      t,
    } = this.props
    const isShowProducts = !lookbook?.hide_top_picks

    // Need this to handle the 2 contents in 1 row case. This also prevent duplicated contents
    const displayedLookbookIds = []
    const isInfluencerLookBook =
      lookbook?.id_category?.toString() === env.LOOKBOOK_INFLUENCER_CATEGORY_ID

    return (
      <React.Fragment>
        {banner && banner.image_cover && <LookbookHeader lookbook={lookbook} />}
        <div className="lookbook-content-items">
          {items &&
            items.map((item, idx) => {
              const { id_lookbooks_content: id } = item
              const item2 = items[idx + 1]

              if (displayedLookbookIds.includes(id)) {
                return null
              }

              displayedLookbookIds.push(id)

              // Tablet-up: In the scenario of two contents per row (Note: some lookbook content has colspan = 0)
              if (!isPhone && item.colspan < 2 && item2) {
                const { id_lookbooks_content: id2 } = item2

                displayedLookbookIds.push(id2)

                return (
                  <div className="two-contents" key={idx}>
                    <LookbookContentItem isTwoContents item={item} key={id} />
                    <LookbookContentItem isTwoContents item={item2} key={id2} />
                  </div>
                )
              }

              return <LookbookContentItem item={item} key={id} />
            })}
          {isInfluencerLookBook && (
            <div className="influencer-signup__btn-wrapper">
              <Button
                className="influencer-signup__btn"
                type={BUTTON_TYPES.primary}
                onClick={this.redirectToInfluencerSignup}
              >
                {hasLocizeTranslation(
                  t,
                  'LOOKBOOK_INFLUENCER_BUTTON',
                  'Sign up now',
                )}
              </Button>
            </div>
          )}
        </div>
        {isShowProducts && this.renderAllProducts()}
      </React.Fragment>
    )
  }

  render() {
    const { lookbook, loading } = this.props

    if (!lookbook && !loading) {
      Router.push(
        '/',
        `/${StateStorage.getShop()}/${StateStorage.getLanguage()}`,
      )
    }

    return (
      <CommonLayout
        meta={this.getPageMeta()}
        pageName={PAGE_NAME.lookbook}
        pageId={lookbook?.id_category}
        showAdsBanner
      >
        <style jsx>{styles}</style>
        <div className="content-wrapper">
          {!lookbook || loading ? (
            <div className="loader-container">
              <Loader />
            </div>
          ) : (
            this.renderContents()
          )}
        </div>
      </CommonLayout>
    )
  }
}

Lookbook.defaultProps = {
  loading: true,
  lookbook: null,
}

Lookbook.propTypes = {
  internationalization: PropTypes.shape({}).isRequired,
  isPhone: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  lookbook: PropTypes.shape({}),
  t: PropTypes.func.isRequired,
}

export { Lookbook }

export default compose(
  connect((state) => ({
    internationalization: state.internationalization,
    isPhone: state.device.isPhone,
    loading: state.lookbook.isFetching,
    lookbook: state.lookbook.payload,
  })),
  withI18next(),
)(Lookbook)
